import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Form, Button, Row, Col, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import SecureApi from '../../../utils/secureApi';
import { isSuperAdmin } from '../../../utils/roleBasedAccess';
import './AddClubDetails.css';
import { getImageUrl } from '../../../utils/imageUrl';

interface AddClubDetailsProps {
  isViewMode?: boolean;
  isEditMode?: boolean;
  initialData?: any;
  onUpdate?: (data: any) => Promise<void>;
  onCancel?: () => void;
  showBreadcrumbs?: boolean;
  showHeader?: boolean;
  actionButtons?: React.ReactNode;
  hideLoginCredentials?: boolean;
  containerPadding?: boolean;
  clubLogo?: string;
  clubName?: string;
  onFormChange?: (hasChanges: boolean) => void;
  onSaveClick?: () => void;
}

const AddClubDetails: React.FC<AddClubDetailsProps> = ({ 
  isViewMode = false, 
  isEditMode = false, 
  initialData, 
  onUpdate, 
  onCancel,
  showBreadcrumbs = true,
  showHeader = true,
  actionButtons,
  hideLoginCredentials = false,
  containerPadding = true,
  clubLogo,
  clubName,
  onFormChange,
  onSaveClick
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    clubName: initialData?.name || '',
    managerName: initialData?.managerName || '',
    contactNumber: initialData?.managerContactNum?.toString() || '',
    location: initialData?.location || '',
    loginEmail: initialData?.emailId || '',
    password: '',
    confirmPassword: '',
    logo: initialData?.logo || '',
    // ✅ NEW FIELDS
    address: initialData?.address || '',
    gstNumber: initialData?.gstNum || ''  // Map gstNum from API to gstNumber in form
  });
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Location dropdown state
  const [locationOptions] = useState([
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Others', label: 'Others' }
  ]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [customLocation, setCustomLocation] = useState('');

  // Logo download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Get user data for permission check
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData = {
    ...user,
    subRole: user.subRole || role,
    permissions: permissions
  };
  const isSuperAdminUser = isSuperAdmin(userData);

  // Track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState({
    clubName: initialData?.name || '',
    managerName: initialData?.managerName || '',
    contactNumber: initialData?.managerContactNum?.toString() || '',
    location: initialData?.location || '',
    loginEmail: initialData?.emailId || '',
    logo: initialData?.logo || '',
    // ✅ Include optional fields in initial state
    address: initialData?.address || '',
    gstNumber: initialData?.gstNum || ''  // Map gstNum from API to gstNumber in form
  });

  // Update original form data when initialData changes
  useEffect(() => {
    if (initialData) {
      const newOriginalData = {
        clubName: initialData.name || '',
        managerName: initialData.managerName || '',
        contactNumber: initialData.managerContactNum?.toString() || '',
        location: initialData.location || '',
        loginEmail: initialData.emailId || '',
        logo: initialData.logo || '',
        // ✅ NEW FIELDS
        address: initialData.address || '',
        gstNumber: initialData.gstNum || ''  // Map gstNum from API to gstNumber in form
      };
      setOriginalFormData(newOriginalData);
      
      // Also update the current form data
      setFormData(prev => ({
        ...prev,
        ...newOriginalData
      }));
    }
  }, [initialData]);

  // Check if form data has changed (for edit mode)
  const hasFormChanges = useMemo(() => {
    if (!isEditMode) return false;
    
    return (
      formData.clubName !== originalFormData.clubName ||
      formData.managerName !== originalFormData.managerName ||
      formData.contactNumber !== originalFormData.contactNumber ||
      formData.location !== originalFormData.location ||
      formData.loginEmail !== originalFormData.loginEmail ||
      formData.logo !== originalFormData.logo ||
      // ✅ Include optional fields in change detection
      formData.address !== originalFormData.address ||
      formData.gstNumber !== originalFormData.gstNumber
    );
  }, [formData, originalFormData, isEditMode]);

  // Notify parent component of form changes
  useEffect(() => {
    if (onFormChange) {
      onFormChange(hasFormChanges);
    }
  }, [hasFormChanges, onFormChange]);

  // Mark all fields as touched when in edit mode
  useEffect(() => {
    if (isEditMode) {
      setTouchedFields(new Set(['clubName', 'managerName', 'contactNumber', 'location', 'loginEmail']));
    }
  }, [isEditMode]);

  // Initialize location dropdown state when initialData changes
  useEffect(() => {
    if (initialData && initialData.location) {
      const isCustomLocation = !['Mumbai', 'Pune'].includes(initialData.location);
      if (isCustomLocation) {
        setSelectedLocation('Others');
        setCustomLocation(initialData.location);
      } else {
        setSelectedLocation(initialData.location);
        setCustomLocation(initialData.location);
      }
    }
  }, [initialData]);

  // Initialize logo preview from initialData when in edit mode
  useEffect(() => {
    if (initialData && (isEditMode || isViewMode)) {
      if (initialData.logo) {
        const logoUrl = initialData.logo.startsWith('http') 
          ? initialData.logo 
          : getImageUrl(initialData.logo);
        setLogoPreview(logoUrl);
      }
    }
  }, [initialData, isEditMode, isViewMode]);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContactNumber = (contact: string): boolean => {
    // Remove all non-digits
    const digitsOnly = contact.replace(/\D/g, '');
    // Accept 10 digits (standard Indian mobile) or 11 digits (with country code)
    return digitsOnly.length === 10 || digitsOnly.length === 11;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(name));
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Location dropdown handlers
  const handleLocationChange = (selectedOption: any) => {
    setSelectedLocation(selectedOption.value);
    if (selectedOption.value === 'Others') {
      setCustomLocation('');
    } else {
      setCustomLocation(selectedOption.value);
      setFormData(prev => ({ ...prev, location: selectedOption.value }));
    }
    // Mark location field as touched
    setTouchedFields(prev => new Set(prev).add('location'));
  };

  const handleCustomLocationChange = (value: string) => {
    setCustomLocation(value);
    setFormData(prev => ({ ...prev, location: value }));
    // Mark location field as touched
    setTouchedFields(prev => new Set(prev).add('location'));
  };

  const validateField = (name: string, value: string): string => {
    const trimmedValue = value.trim();
    
    // Only validate if field has been touched or if we're in edit mode
    const shouldValidate = touchedFields.has(name) || isEditMode;
    
    // Check for empty spaces only
    if (trimmedValue.length === 0 && value.length > 0) {
      return 'This field cannot contain only spaces';
    }
    
    // Field-specific validations
    switch (name) {
      case 'clubName':
      case 'managerName':
        if (trimmedValue.length === 0) {
          return 'This field is required';
        }
        break;
        
      case 'location':
        if (selectedLocation === 'Others' && !customLocation.trim()) {
          return 'Please enter custom location';
        }
        if (!selectedLocation) {
          return 'Location is required';
        }
        break;
        
      case 'address':
        if (trimmedValue.length === 0) {
          return 'Address is required';
        }
        break;
        
      case 'contactNumber':
        if (trimmedValue.length === 0) {
          return 'Contact number is required';
        }
        if (shouldValidate && !validateContactNumber(trimmedValue)) {
          return 'Please enter a valid 10-digit or 11-digit contact number';
        }
        break;
        
      case 'loginEmail':
        if (trimmedValue.length === 0) {
          return 'Email ID is required';
        }
        if (shouldValidate && !validateEmail(trimmedValue)) {
          return 'Please enter a valid email address';
        }
        break;
        
      case 'password':
        if (trimmedValue.length === 0) {
          return 'Password is required';
        }
        if (shouldValidate && trimmedValue.length < 8) {
          return 'Password must be at least 8 characters long';
        }
        break;
        
      case 'confirmPassword':
        if (trimmedValue.length === 0) {
          return 'Please confirm your password';
        }
        if (shouldValidate && formData.password && trimmedValue !== formData.password) {
          return 'Passwords do not match. Please check both password fields.';
        }
        break;
    }
    
    return '';
  };

  // Check if passwords match
  const passwordsMatch = useMemo(() => {
    return formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  }, [formData.password, formData.confirmPassword]);

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoUpload = async (file: File) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    setLogoError(null);
    setIsLogoUploading(true);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);

    try {
      // Get compression options based on file size
      const sizeKB = file.size / 1024;
      const maxWidthOrHeight = 1600;
      
      let compressionOptions;
      if (sizeKB < 120) {
        // Small images: skip compression
        compressionOptions = null;
      } else {
        // High quality compression for club logos
        compressionOptions = { 
          maxSizeMB: 0.5, 
          maxWidthOrHeight, 
          initialQuality: 0.92, 
          useWebWorker: true,
          onProgress: (progress: number) => {
            setLogoUploadProgress(progress);
          }
        };
      }

      // Compress the image if needed
      let processedFile = file;
      if (compressionOptions) {
        processedFile = await imageCompression(file, compressionOptions);
      }

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = processedFile.type.split('/')[1] || 'jpg';
      const fileName = `club_logo_${Date.now()}.${ext}`;
      const context = `my-club/club-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await SecureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: processedFile.type,
          context,
        }
      );

      // Handle both SecureApi and regular axios response formats
      const presignedUrl = presignRes.data?.presignedUrl || presignRes.presignedUrl;
      const s3Key = presignRes.data?.key || presignRes.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': processedFile.type },
        body: processedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new logo
      setFormData(prev => ({ ...prev, logo: s3Key }));
      setLogoPreview(getImageUrl(s3Key));
      setLogoUploadSuccess(true);
      
    } catch (err: any) {
      setLogoError('Logo upload failed. Please try again.');
    } finally {
      setIsLogoUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  // Handle logo download for super admins
  const handleLogoDownload = async () => {
    if (!formData.logo || !isSuperAdminUser) return;
    
    try {
      setIsDownloading(true);
      setDownloadError(null);
      
      // Construct the full S3 URL
      const logoUrl = formData.logo.startsWith('http') 
        ? formData.logo 
        : getImageUrl(formData.logo);
      
      // Fetch the image
      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error('Failed to fetch logo');
      
      const blob = await response.blob();
      
      // Extract filename from the logo path or create a default one
      const filename = formData.logo.includes('/') 
        ? formData.logo.split('/').pop() 
        : `club_logo_${initialData?.name || 'download'}.${blob.type.split('/')[1] || 'jpg'}`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'club_logo.jpg';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Logo download error:', error);
      setDownloadError('Failed to download logo. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Generate a secure password
  const generatePassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest with random characters
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setFormData(prev => ({
      ...prev,
      password: password,
      confirmPassword: password
    }));
  };

  // Validate form fields and return validation results
  const validateForm = useMemo(() => {
    const validations = {
      clubName: validateField('clubName', formData.clubName),
      managerName: validateField('managerName', formData.managerName),
      contactNumber: validateField('contactNumber', formData.contactNumber),
      loginEmail: validateField('loginEmail', formData.loginEmail),
      // Only validate password fields if not in edit mode (since they're hidden)
      ...(isEditMode ? {} : {
        password: validateField('password', formData.password),
        confirmPassword: validateField('confirmPassword', formData.confirmPassword)
      }),
      location: validateField('location', formData.location),
      address: validateField('address', formData.address)
    };

    return validations;
  }, [formData, touchedFields, isEditMode]);

  // Check if form is valid (all required fields filled)
  const isFormValid = useMemo(() => {
    const validations = validateForm;
    const hasErrors = Object.values(validations).some(error => error !== '');
    
    // Additional location validation for dropdown
    if (selectedLocation === 'Others' && !customLocation.trim()) {
      return false;
    }
    if (!selectedLocation) {
      return false;
    }
    
    return !hasErrors;
  }, [validateForm, selectedLocation, customLocation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Validate form
      if (!isFormValid) {
        throw new Error('Please fill all required fields correctly');
      }

      console.log('Form validation passed. Form data:', formData);

      // Validate password match only if not in edit mode
      if (!isEditMode) {
        if (formData.password !== formData.confirmPassword) {
          console.log('Password mismatch:', {
            password: formData.password,
            confirmPassword: formData.confirmPassword,
            passwordLength: formData.password?.length,
            confirmPasswordLength: formData.confirmPassword?.length
          });
          throw new Error('Passwords do not match. Please ensure both password fields are identical.');
        }

        // Additional validation to ensure passwords are not empty
        if (!formData.password || !formData.confirmPassword) {
          throw new Error('Both password fields are required');
        }
      }

      // Validate all required fields
      if (!formData.clubName?.trim()) {
        throw new Error('Club name is required');
      }
      if (!formData.managerName?.trim()) {
        throw new Error('Manager name is required');
      }
      if (!formData.contactNumber?.trim()) {
        throw new Error('Contact number is required');
      }
      if (!formData.loginEmail?.trim()) {
        throw new Error('Email ID is required');
      }
      if (!formData.location?.trim()) {
        throw new Error('Location is required');
      }
      if (!formData.address?.trim()) {
        throw new Error('Address is required');
      }

      // Prepare API request body
      const managerContactNum = parseInt(formData.contactNumber.replace(/\D/g, ''));
      
      // Validate contact number
      if (isNaN(managerContactNum) || managerContactNum <= 0) {
        throw new Error('Please enter a valid contact number');
      }

      const requestBody: any = {
        name: formData.clubName,
        emailId: formData.loginEmail,
        // Only include password fields if not in edit mode
        ...(isEditMode ? {} : {
          password: formData.password,
          confirmPassword: formData.confirmPassword
        }),
        managerName: formData.managerName,
        countryCode: 91, // Default to India
        managerContactNum: managerContactNum,
        location: formData.location || '',
        isActive: true,
        // ✅ NEW FIELDS
        address: formData.address || '',
        gstNum: formData.gstNumber || ''  // Map gstNumber to gstNum for API
      };

      // Only add logo field if a logo has been uploaded
      if (formData.logo && formData.logo.trim() !== '') {
        requestBody.logo = formData.logo;
      }

      console.log('API Request Body:', requestBody);

      if (isEditMode && onUpdate) {
        // Update existing club
        await onUpdate(requestBody);
      } else {
        // Create new club
        const response = await SecureApi.post(
          `/api/v1/club/add-club`,
          requestBody
        );

        if (response) {
          // Navigate back to All Clubs page on success
          navigate('/all-clubs');
        }
      }
    } catch (error: any) {
      console.error('Error creating club:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      
      // Extract more detailed error message
      let errorMessage = 'Failed to create club. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate('/all-clubs');
  };

  const breadcrumbItems = isViewMode || isEditMode ? [
    { label: 'All Clubs', onClick: () => navigate('/all-clubs') },
    { label: 'Club Details', active: true }
  ] : [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'All Clubs', onClick: () => navigate('/all-clubs') },
    { label: 'Add New Club', active: true }
  ];

  return (
    <div className="add-club-details-page">
      <Container fluid className={containerPadding ? 'py-4' : ''}>
        {/* Show shimmer loader during submission */}
        {isSubmitting && (
          <div className="form-loading-overlay">
            <ShimmerLoader type="form" />
          </div>
        )}
        
        {/* Breadcrumbs */}
        {showBreadcrumbs && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        )}

        {/* Page Header with Back Button and Title */}
        {showHeader && (
          <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
            <div className="d-flex align-items-center" style={{ gap: '16px', marginBottom: '24px' }}>
              <button 
                className="back-button"
                onClick={handleGoBack}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              {/* Show club logo if provided (Edit/View mode) */}
              {clubLogo && (
                <div className="club-logo-small" style={{ flexShrink: 0 }}>
                  <img 
                    src={clubLogo}
                    alt="Club Logo"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('d-none');
                    }}
                  />
                  <div className="default-club-logo-small d-none">
                    <i className="ri-building-line"></i>
                  </div>
                </div>
              )}
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0">
                  {clubName || (isViewMode ? 'Club Details' : isEditMode ? 'Edit Club' : 'Add New Club')}
                </h1>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Card Layout similar to Club Details */}
        <div style={{ padding: containerPadding ? '0 15px' : '0' }}>
          <div className="club-details-card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="card-title-section">
                  <h4 className="mb-0">Club Details</h4>
                </div>
                {actionButtons && (
                  <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    {actionButtons}
                  </div>
                )}
              </div>

              {/* Form Content */}
              <Form onSubmit={handleSubmit}>
                {/* Different layout for Add vs Edit/View mode */}
                {!isViewMode && !isEditMode ? (
                  // Add New Club mode - with nested card structure
                  <div className="club-form-card mb-4">
                    <div className="card-body">
                      
                      {/* Upload Club Logo */}
                      <div className="logo-upload-section mb-4">
                        <Form.Label className="form-label">
                          Upload Club Logo
                        </Form.Label>
                        <div className={`logo-upload-area logo-upload-left-aligned ${isViewMode ? 'disabled' : ''}`} onClick={isViewMode ? undefined : handleLogoClick}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={handleFileChange}
                            className="logo-upload-input"
                            style={{ display: 'none' }}
                          />
                          {logoPreview ? (
                            <div className="logo-preview">
                              <img src={logoPreview} alt="Club Logo Preview" />
                              <div className="logo-overlay">
                                <i className="ri-camera-line"></i>
                              </div>
                            </div>
                          ) : (
                            <div className="logo-upload-label">
                              <i className="ri-image-add-line upload-icon"></i>
                            </div>
                          )}
                          {isLogoUploading && (
                            <div className="logo-upload-progress">
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill" 
                                  style={{ width: `${logoUploadProgress}%` }}
                                ></div>
                              </div>
                              <span className="progress-text">{logoUploadProgress}%</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Logo upload error and success */}
                        {logoError && (
                          <div className="document-upload-error">
                            <i className="ri-error-warning-line"></i>
                            {logoError}
                          </div>
                        )}
                        
                        {logoUploadSuccess && (
                          <div className="document-upload-success">
                            <i className="ri-check-line"></i>
                            Club logo uploaded successfully!
                          </div>
                        )}

                        {/* Logo download button for super admins */}
                        {logoPreview && isSuperAdminUser && (isViewMode || isEditMode) && (
                          <div className="logo-actions mt-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleLogoDownload}
                              disabled={isDownloading}
                              className="me-2"
                            >
                              {isDownloading ? (
                                <>
                                  <i className="ri-download-line me-1"></i>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <i className="ri-download-line me-1"></i>
                                  Download Logo
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {downloadError && (
                          <div className="alert alert-danger mt-2" role="alert">
                            <i className="ri-error-warning-line me-2"></i>
                            {downloadError}
                          </div>
                        )}
                      </div>

                      {/* Club Details Section */}
                      <div className="club-details-section mb-4">
                        <Row>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                Club/Team Name <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="clubName"
                                placeholder="Enter Club Name"
                                value={formData.clubName}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className={`form-control ${(touchedFields.has('clubName') || isEditMode) && validateField('clubName', formData.clubName) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('clubName') || isEditMode) && validateField('clubName', formData.clubName) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('clubName', formData.clubName)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                Club/Team Manager Name <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="managerName"
                                placeholder="Enter Team/Club Manager Name"
                                value={formData.managerName}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className={`form-control ${(touchedFields.has('managerName') || isEditMode) && validateField('managerName', formData.managerName) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('managerName') || isEditMode) && validateField('managerName', formData.managerName) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('managerName', formData.managerName)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                Contact Number <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="contactNumber"
                                placeholder="Enter Contact Number"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className={`form-control ${(touchedFields.has('contactNumber') || isEditMode) && validateField('contactNumber', formData.contactNumber) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('contactNumber') || isEditMode) && validateField('contactNumber', formData.contactNumber) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('contactNumber', formData.contactNumber)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label required-field">
                                Location
                              </Form.Label>
                              <Select
                                value={locationOptions.find(option => option.value === selectedLocation)}
                                onChange={handleLocationChange}
                                options={locationOptions}
                                placeholder="Select Location"
                                isDisabled={isViewMode}
                                className={validateForm.location ? 'is-invalid' : ''}
                                styles={{
                                  control: (provided, state) => ({
                                    ...provided,
                                    borderColor: validateForm.location ? '#dc3545' : state.isFocused ? '#2563eb' : '#d1d5db',
                                    boxShadow: validateForm.location ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : state.isFocused ? '0 0 0 0.2rem rgba(37, 99, 235, 0.25)' : 'none',
                                    '&:hover': {
                                      borderColor: validateForm.location ? '#dc3545' : '#9ca3af'
                                    }
                                  })
                                }}
                              />
                              {selectedLocation === 'Others' && (
                                <Form.Control
                                  type="text"
                                  value={customLocation}
                                  onChange={(e) => handleCustomLocationChange(e.target.value)}
                                  placeholder="Enter custom location"
                                  disabled={isViewMode}
                                  className={`form-control custom-location-input ${validateForm.location ? 'is-invalid' : ''}`}
                                />
                              )}
                              {validateForm.location && (
                                <div className="invalid-feedback d-block">
                                  {validateForm.location}
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        {/* ✅ NEW ROW: Address and GST Number */}
                        <Row className="mt-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="form-label required-field">
                                Address
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                name="address"
                                placeholder="Enter Club Address"
                                value={formData.address}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                rows={3}
                                className={`form-control ${(touchedFields.has('address') || isEditMode) && validateField('address', formData.address) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('address') || isEditMode) && validateField('address', formData.address) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('address', formData.address)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                GST Number (Optional)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="gstNumber"
                                placeholder="Enter GST Number"
                                value={formData.gstNumber}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className="form-control"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>

                      {/* Login Credentials Section - Only show in Add mode */}
                      {!isViewMode && !isEditMode && !hideLoginCredentials && (
                        <div className="login-credentials-section">
                          <h5 className="section-title mb-4">Login Credentials</h5>
                          <div className="password-requirements mb-3">
                            <small className="text-muted">
                              Password must be at least 8 characters, include 1 uppercase, 1 number, and 1 special character.
                            </small>
                          </div>
                          <Row>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="form-label">
                                  Email ID <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                  type="email"
                                  name="loginEmail"
                                  placeholder="Enter Email ID"
                                  value={formData.loginEmail}
                                  onChange={handleInputChange}
                                  disabled={isViewMode}
                                  className={`form-control ${(touchedFields.has('loginEmail') || isEditMode) && validateField('loginEmail', formData.loginEmail) ? 'is-invalid' : ''}`}
                                />
                                {(touchedFields.has('loginEmail') || isEditMode) && validateField('loginEmail', formData.loginEmail) && (
                                  <Form.Control.Feedback type="invalid">
                                    {validateField('loginEmail', formData.loginEmail)}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="form-label">
                                  Password <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="password-input-wrapper">
                                  <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Create password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className={`form-control ${(touchedFields.has('password') || isEditMode) && validateField('password', formData.password) ? 'is-invalid' : ''}`}
                                  />
                                  <div className="password-buttons">
                                    <button
                                      type="button"
                                      className="generate-password-btn"
                                      onClick={generatePassword}
                                      title="Generate password"
                                    >
                                      <i className="ri-refresh-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="password-toggle-btn"
                                      onClick={() => setShowPassword(!showPassword)}
                                      title={showPassword ? "Hide password" : "Show password"}
                                    >
                                      <i className={`ri-eye-${showPassword ? 'line' : 'off-line'}`}></i>
                                    </button>
                                  </div>
                                </div>
                                {(touchedFields.has('password') || isEditMode) && validateField('password', formData.password) && (
                                  <Form.Control.Feedback type="invalid">
                                    {validateField('password', formData.password)}
                                  </Form.Control.Feedback>
                                )}
                                {(touchedFields.has('password') || isEditMode) && formData.password && !validateField('password', formData.password) && (
                                  <div className="text-success small mt-1">
                                    <i className="ri-check-line me-1"></i>
                                    Password meets requirements
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="form-label">
                                  Confirm Password <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="password-input-wrapper single-button">
                                  <Form.Control
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    placeholder="Re-enter password"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className={`form-control ${(touchedFields.has('confirmPassword') || isEditMode) && validateField('confirmPassword', formData.confirmPassword) ? 'is-invalid' : ''}`}
                                  />
                                  <div className="password-buttons single-button">
                                    <button
                                      type="button"
                                      className="password-toggle-btn"
                                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                      title={showConfirmPassword ? "Hide password" : "Show password"}
                                    >
                                      <i className={`ri-eye-${showConfirmPassword ? 'line' : 'off-line'}`}></i>
                                    </button>
                                  </div>
                                </div>
                                {(touchedFields.has('confirmPassword') || isEditMode) && validateField('confirmPassword', formData.confirmPassword) && (
                                  <Form.Control.Feedback type="invalid">
                                    {validateField('confirmPassword', formData.confirmPassword)}
                                  </Form.Control.Feedback>
                                )}
                                {formData.confirmPassword && !validateField('confirmPassword', formData.confirmPassword) && passwordsMatch && (
                                  <div className="text-success small mt-1">
                                    <i className="ri-check-line me-1"></i>
                                    Passwords match
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Edit/View mode - using the same nested card structure as Add New Club
                  <div className="club-form-card mb-4">
                    <div className="card-body">
                      
                      {/* Upload Club Logo */}
                      <div className="logo-upload-section mb-4">
                        <Form.Label className="form-label">
                          Upload Club Logo
                        </Form.Label>
                        <div className={`logo-upload-area logo-upload-left-aligned ${isViewMode ? 'disabled' : ''}`} onClick={isViewMode ? undefined : handleLogoClick}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={handleFileChange}
                            className="logo-upload-input"
                            style={{ display: 'none' }}
                          />
                          {logoPreview ? (
                            <div className="logo-preview">
                              <img src={logoPreview} alt="Club Logo Preview" />
                              <div className="logo-overlay">
                                <i className="ri-camera-line"></i>
                              </div>
                            </div>
                          ) : (
                            <div className="logo-upload-label">
                              <i className="ri-image-add-line upload-icon"></i>
                            </div>
                          )}
                          {isLogoUploading && (
                            <div className="logo-upload-progress">
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill" 
                                  style={{ width: `${logoUploadProgress}%` }}
                                ></div>
                              </div>
                              <span className="progress-text">{logoUploadProgress}%</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Logo upload error and success */}
                        {logoError && (
                          <div className="document-upload-error">
                            <i className="ri-error-warning-line"></i>
                            {logoError}
                          </div>
                        )}
                        
                        {logoUploadSuccess && (
                          <div className="document-upload-success">
                            <i className="ri-check-line"></i>
                            Club logo uploaded successfully!
                          </div>
                        )}

                        {/* Logo download button for super admins */}
                        {logoPreview && isSuperAdminUser && (isViewMode || isEditMode) && (
                          <div className="logo-actions mt-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleLogoDownload}
                              disabled={isDownloading}
                              className="me-2"
                            >
                              {isDownloading ? (
                                <>
                                  <i className="ri-download-line me-1"></i>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <i className="ri-download-line me-1"></i>
                                  Download Logo
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {downloadError && (
                          <div className="alert alert-danger mt-2" role="alert">
                            <i className="ri-error-warning-line me-2"></i>
                            {downloadError}
                          </div>
                        )}
                      </div>

                      {/* Club Details Section */}
                      <div className="club-details-section mb-4">
                        <Row>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                Club/Team Name <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="clubName"
                                placeholder="Enter Club Name"
                                value={formData.clubName}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className={`form-control ${(touchedFields.has('clubName') || isEditMode) && validateField('clubName', formData.clubName) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('clubName') || isEditMode) && validateField('clubName', formData.clubName) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('clubName', formData.clubName)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                Club/Team Manager Name <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="managerName"
                                placeholder="Enter Team/Club Manager Name"
                                value={formData.managerName}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className={`form-control ${(touchedFields.has('managerName') || isEditMode) && validateField('managerName', formData.managerName) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('managerName') || isEditMode) && validateField('managerName', formData.managerName) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('managerName', formData.managerName)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                Contact Number <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="contactNumber"
                                placeholder="Enter Contact Number"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className={`form-control ${(touchedFields.has('contactNumber') || isEditMode) && validateField('contactNumber', formData.contactNumber) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('contactNumber') || isEditMode) && validateField('contactNumber', formData.contactNumber) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('contactNumber', formData.contactNumber)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label className="form-label required-field">
                                Location
                              </Form.Label>
                              <Select
                                value={locationOptions.find(option => option.value === selectedLocation)}
                                onChange={handleLocationChange}
                                options={locationOptions}
                                placeholder="Select Location"
                                isDisabled={isViewMode}
                                className={validateForm.location ? 'is-invalid' : ''}
                                styles={{
                                  control: (provided, state) => ({
                                    ...provided,
                                    borderColor: validateForm.location ? '#dc3545' : state.isFocused ? '#2563eb' : '#d1d5db',
                                    boxShadow: validateForm.location ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : state.isFocused ? '0 0 0 0.2rem rgba(37, 99, 235, 0.25)' : 'none',
                                    '&:hover': {
                                      borderColor: validateForm.location ? '#dc3545' : '#9ca3af'
                                    }
                                  })
                                }}
                              />
                              {selectedLocation === 'Others' && (
                                <Form.Control
                                  type="text"
                                  value={customLocation}
                                  onChange={(e) => handleCustomLocationChange(e.target.value)}
                                  placeholder="Enter custom location"
                                  disabled={isViewMode}
                                  className={`form-control custom-location-input ${validateForm.location ? 'is-invalid' : ''}`}
                                />
                              )}
                              {validateForm.location && (
                                <div className="invalid-feedback d-block">
                                  {validateForm.location}
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        {/* ✅ NEW ROW: Address and GST Number for Edit/View mode */}
                        <Row className="mt-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="form-label required-field">
                                Address
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                name="address"
                                placeholder="Enter Club Address"
                                value={formData.address}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                rows={3}
                                className={`form-control ${(touchedFields.has('address') || isEditMode) && validateField('address', formData.address) ? 'is-invalid' : ''}`}
                              />
                              {(touchedFields.has('address') || isEditMode) && validateField('address', formData.address) && (
                                <Form.Control.Feedback type="invalid">
                                  {validateField('address', formData.address)}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="form-label">
                                GST Number (Optional)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="gstNumber"
                                placeholder="Enter GST Number"
                                value={formData.gstNumber}
                                onChange={handleInputChange}
                                disabled={isViewMode}
                                className="form-control"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {submitError && (
                  <div className="mb-3">
                    <div className="alert alert-danger">
                      <i className="ri-error-warning-line me-2"></i>
                      {submitError}
                    </div>
                  </div>
                )}

                {/* Action Button - Only show for Add New Club mode */}
                {!isViewMode && !isEditMode && (
                  <div className="action-buttons-section">
                    <Button
                      type="submit"
                      variant="primary"
                      className="create-club-btn"
                      disabled={isSubmitting || !isFormValid}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        'Create & Send Credentials'
                      )}
                    </Button>
                  </div>
                )}
              </Form>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AddClubDetails;